import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthLayout from '../../components/auth/AuthLayout';
import AuthInput from '../../components/ui/AuthInput';
import AuthButton from '../../components/ui/AuthButton';
import PasswordStrengthIndicator from '../../components/auth/PasswordStrengthIndicator';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext'; // Assuming you'll have an AuthContext

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth(); // From your AuthContext
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState(''); // Add phone state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [passwordCriteria, setPasswordCriteria] = useState({
    minLength: false,
    uppercase: false,
    lowercase: false,
    number: false,
    symbol: false,
  });

  useEffect(() => {
    setPasswordCriteria({
      minLength: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      symbol: /[^A-Za-z0-9]/.test(password),
    });
  }, [password]);

  const allPasswordCriteriaMet = Object.values(passwordCriteria).every(Boolean);

  const validateForm = () => {
    const newErrors = {};
    if (!fullName.trim()) newErrors.fullName = 'Full Name is required.';
    if (!email.trim()) {
      newErrors.email = 'Email Address is required.';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email address is invalid.';
    }
    // Basic phone validation (optional based on your needs)
    if (phone.trim() && !/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/.test(phone)) {
        newErrors.phone = 'Phone number is invalid.';
    }
    if (!password) {
      newErrors.password = 'Password is required.';
    } else if (!allPasswordCriteriaMet) {
      newErrors.password = 'Password does not meet all criteria.';
    }
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Confirm Password is required.';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match.';
    }
    if (!agreedToTerms) newErrors.terms = 'You must agree to the Terms of Service and Privacy Policy.';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setApiError('');
    setSuccessMessage('');

    if (!validateForm()) {
      return;
    }
    setIsLoading(true);
    setErrors({});

    // --- API Call Simulation ---
    try {
      // Pass fullName as username, or ensure your backend handles/maps it if it expects a different field.
      await register(fullName, email, phone, password); // Add phone to register call. Assuming role defaults to 'client'
      // await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate API
      // console.log('Registration attempt with:', { fullName, email, phone, password, agreedToTerms });
      setSuccessMessage('Account created successfully! Please log in.');
      setTimeout(() => navigate('/login'), 2000); // Navigate after a short delay
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Registration failed. Please try again.";
      // const errorMessage = "Registration failed. Email may already be in use (simulated).";
      setApiError(errorMessage);
    } finally {
      setIsLoading(false);
    }
    // --- End API Call Simulation ---
  };

  return (
    <AuthLayout title="Create Your Quality Air Houston Account">
      <form onSubmit={handleRegister} className="space-y-6">
        {apiError && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4 rounded-md">
            <p className="text-sm text-red-700">{apiError}</p>
          </div>
        )}
        {successMessage && (
           <div className="bg-green-50 border-l-4 border-green-400 p-4">
            <p className="text-sm text-green-700">{successMessage}</p>
          </div>
        )}

        <AuthInput
          label="Full Name"
          name="fullName"
          type="text"
          placeholder="e.g., Jane Doe"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          error={errors.fullName}
          required
        />
        <AuthInput
          label="Email Address"
          name="email"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email}
          required
        />
        <AuthInput
          label="Phone Number (Optional)"
          name="phone"
          type="tel"
          autoComplete="tel"
          placeholder="(XXX) XXX-XXXX"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <AuthInput
          label="Create Password"
          name="password"
          type={isPasswordVisible ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={errors.password}
          required
          showPasswordToggle
          isPasswordVisible={isPasswordVisible}
          onTogglePasswordVisibility={() => setIsPasswordVisible(!isPasswordVisible)}
        />
        
        <PasswordStrengthIndicator password={password} />

        <AuthInput
          label="Confirm Password"
          name="confirmPassword"
          type={isConfirmPasswordVisible ? "text" : "password"}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={errors.confirmPassword}
          required
          showPasswordToggle
          isPasswordVisible={isConfirmPasswordVisible}
          onTogglePasswordVisibility={() => setIsConfirmPasswordVisible(!isConfirmPasswordVisible)}
        />
        
        <div className="space-y-1">
            <div className="flex items-start">
                <div className="flex items-center h-5">
                    <input
                        id="terms"
                        name="terms"
                        type="checkbox"
                        checked={agreedToTerms}
                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                        className="focus:ring-brand-accent-orange h-4 w-4 text-brand-accent-orange border-gray-300 rounded"
                    />
                </div>
                <div className="ml-3 text-sm">
                    <label htmlFor="terms" className={`font-medium ${errors.terms ? 'text-red-600' : 'text-gray-700'}`}>
                        I agree to the Quality Air Houston{' '}
                        <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-brand-green-dark hover:text-brand-green-darker underline">
                            Terms of Service
                        </a>
                        {' '}&{' '}
                        <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-brand-green-dark hover:text-brand-green-darker underline">
                            Privacy Policy
                        </a>.
                    </label>
                    {errors.terms && <p className="text-xs text-red-600 mt-1">{errors.terms}</p>}
                </div>
            </div>
        </div>

        <AuthButton 
          type="submit" 
          isLoading={isLoading} 
          fullWidth
          disabled={isLoading || !agreedToTerms || !fullName || !email || !password || !confirmPassword || !allPasswordCriteriaMet || password !== confirmPassword}
        >
          Create Account
        </AuthButton>

        <p className="text-sm text-center text-gray-600">
          Already have an account?{' '}
          <Link to="/login" className="font-semibold text-brand-accent-orange hover:text-orange-500">
            Log In
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
};

export default RegisterPage;